import React from "react";
import { motion } from "framer-motion";
import {
  BarChart2,
  TrendingUp,
  AlertCircle,
  DollarSign,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Clock,
} from "lucide-react";

const FeeReportsPage = () => {
  // Mock Data for a richer UI
  const transactions = [
    {
      id: "TX12345",
      student: "Rahul Kumar",
      amount: "₹15,000",
      date: "Today, 10:30 AM",
      status: "Success",
      type: "Tuition",
    },
    {
      id: "TX12346",
      student: "Priya Sharma",
      amount: "₹12,000",
      date: "Yesterday, 2:15 PM",
      status: "Success",
      type: "Exam",
    },
    {
      id: "TX12347",
      student: "Amit Singh",
      amount: "₹5,000",
      date: "12 Dec, 4:45 PM",
      status: "Pending",
      type: "Library",
    },
    {
      id: "TX12348",
      student: "Sneha Gupta",
      amount: "₹25,000",
      date: "12 Dec, 11:20 AM",
      status: "Failed",
      type: "Tuition",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-emerald-600 to-green-600 rounded-2xl shadow-lg shadow-emerald-500/20">
              <BarChart2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-br from-emerald-400 to-green-400 bg-clip-text text-transparent">
                Fee Analytics
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Financial overview and transaction reports
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <select className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg focus:ring-emerald-500 focus:border-emerald-500 p-2.5">
              <option>This Month</option>
              <option>Last Month</option>
              <option>Last 6 Months</option>
              <option>This Year</option>
            </select>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Total Collection */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <Wallet className="w-24 h-24 text-emerald-500" />
            </div>
            <div className="relative z-10">
              <p className="text-gray-400 text-sm font-medium mb-1">
                Total Collection
              </p>
              <h3 className="text-3xl font-bold text-white mb-2">₹25.4L</h3>
              <div className="flex items-center gap-1 text-emerald-400 text-xs font-semibold bg-emerald-500/10 w-fit px-2 py-1 rounded max-w-full">
                <ArrowUpRight className="w-3 h-3" /> +12.5% vs last month
              </div>
            </div>
          </div>

          {/* Pending Dues */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <AlertCircle className="w-24 h-24 text-red-500" />
            </div>
            <div className="relative z-10">
              <p className="text-gray-400 text-sm font-medium mb-1">
                Pending Dues
              </p>
              <h3 className="text-3xl font-bold text-white mb-2">₹4.2L</h3>
              <div className="flex items-center gap-1 text-red-400 text-xs font-semibold bg-red-500/10 w-fit px-2 py-1 rounded">
                <ArrowDownRight className="w-3 h-3" /> +2.3% increase
              </div>
            </div>
          </div>

          {/* Online Payments */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <CreditCard className="w-24 h-24 text-blue-500" />
            </div>
            <div className="relative z-10">
              <p className="text-gray-400 text-sm font-medium mb-1">
                Online Payments
              </p>
              <h3 className="text-3xl font-bold text-white mb-2">₹18.2L</h3>
              <div className="flex items-center gap-1 text-blue-400 text-xs font-semibold bg-blue-500/10 w-fit px-2 py-1 rounded">
                65% of total
              </div>
            </div>
          </div>

          {/* Cash/Offline */}
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 relative overflow-hidden group">
            <div className="absolute right-0 top-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <DollarSign className="w-24 h-24 text-orange-500" />
            </div>
            <div className="relative z-10">
              <p className="text-gray-400 text-sm font-medium mb-1">
                Cash / Offline
              </p>
              <h3 className="text-3xl font-bold text-white mb-2">₹7.2L</h3>
              <div className="flex items-center gap-1 text-orange-400 text-xs font-semibold bg-orange-500/10 w-fit px-2 py-1 rounded">
                35% of total
              </div>
            </div>
          </div>
        </div>

        {/* Charts & Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Chart Area */}
          <div className="lg:col-span-2 bg-gray-800 rounded-2xl p-6 border border-gray-700 min-h-[400px] flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-white">
                Revenue Overview
              </h3>
              <div className="flex gap-2">
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>{" "}
                  Collected
                </span>
                <span className="flex items-center gap-1 text-xs text-gray-400">
                  <div className="w-2 h-2 rounded-full bg-gray-600"></div>{" "}
                  Projected
                </span>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center bg-gray-900/50 rounded-xl border-dashed border border-gray-700">
              <div className="text-center">
                <BarChart2 className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  Revenue Graph Visualization
                </p>
              </div>
            </div>
          </div>

          {/* Recent Transactions List */}
          <div className="bg-gray-800 rounded-2xl p-0 border border-gray-700 overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">
                Recent Transactions
              </h3>
              <button className="text-emerald-400 text-xs font-semibold hover:underline">
                View All
              </button>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[400px] p-2">
              {transactions.map((tx) => (
                <div
                  key={tx.id}
                  className="p-3 hover:bg-gray-700/30 rounded-lg transition-colors flex items-center justify-between group"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        tx.status === "Success"
                          ? "bg-green-500/10 text-green-500"
                          : tx.status === "Pending"
                          ? "bg-yellow-500/10 text-yellow-500"
                          : "bg-red-500/10 text-red-500"
                      }`}
                    >
                      {tx.status === "Success" ? (
                        <ArrowDownRight className="w-4 h-4" />
                      ) : tx.status === "Pending" ? (
                        <Clock className="w-4 h-4" />
                      ) : (
                        <AlertCircle className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {tx.student}
                      </p>
                      <p className="text-xs text-gray-500">
                        {tx.type} • {tx.date}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-white">{tx.amount}</p>
                    <p
                      className={`text-[10px] font-medium ${
                        tx.status === "Success"
                          ? "text-green-400"
                          : tx.status === "Pending"
                          ? "text-yellow-400"
                          : "text-red-400"
                      }`}
                    >
                      {tx.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FeeReportsPage;
